import React, { FC, ReactElement, SyntheticEvent, useEffect, useRef, useState } from "react";

import { Alignment, Dialog, FaIcons, IconButton } from "@in/component-library";
import cn from "classnames";

export enum AlignmentHorizontal {
  Left = "left",
  Right = "right",
  Center = "center"
}

interface DropdownMenuProps {
  items: ReactElement<HTMLButtonElement>[];
  listPosition?: "left" | "right";
  isMobile?: boolean;
  align?: AlignmentHorizontal;
  isOpen?: boolean;
  activeItem?: number | null;
}

const DropdownMenu: FC<DropdownMenuProps> = ({
  items,
  listPosition = "left",
  isMobile,
  align = AlignmentHorizontal.Center,
  isOpen = false,
  activeItem = null
}) => {
  const [open, setOpen] = useState(isOpen);
  const [activeItemIndex, setActiveItemIndex] = useState<number | null>(activeItem);
  const itemListRef = useRef<HTMLUListElement>(null);
  const buttonGroupRef = useRef<HTMLDivElement>(null);
  const menuButtonNeedFocus = useRef<boolean>(false);

  // Manage click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const targetElement = event.target as HTMLElement;
      const buttonGroup = buttonGroupRef.current;
      const itemList = itemListRef.current;
      if (itemList && !itemList.contains(targetElement) && buttonGroup && !buttonGroup.contains(targetElement)) {
        setOpen(false);
        setActiveItemIndex(null);

        // Give focus back to the menu button, except for when the target element is a button
        if (targetElement && targetElement.tagName !== "BUTTON") {
          menuButtonNeedFocus.current = true;
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [itemListRef]);

  const handleMenuButtonClick = (event: SyntheticEvent) => {
    event.preventDefault();
    setOpen(!open);
    if (activeItemIndex === null) {
      // Delay setting the active item index.
      // Fixes Firefox NVDA bug where the announcment of the menu interferes with the announcement of other elements.
      setTimeout(() => {
        setActiveItemIndex(0);
      }, 50); // Delay of 50 millisecond
    } else {
      setActiveItemIndex(null);
    }
  };

  const handleKeydown: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
    if (open && itemListRef.current) {
      const items = itemListRef.current.children;
      const itemsLength = items.length;

      switch (event.key) {
        case "Tab":
          event.preventDefault();
          if (!event.shiftKey) {
            setActiveItemIndex((prev) => {
              if (prev === null) {
                return 0;
              } else if (prev < itemsLength - 1) {
                return prev + 1;
              } else {
                setOpen(false);
                menuButtonNeedFocus.current = true;
                return null;
              }
            });
          } else {
            setActiveItemIndex((prev) => {
              if (prev === null) {
                return itemsLength - 1;
              } else if (prev > 0) {
                return prev - 1;
              } else {
                setOpen(false);
                menuButtonNeedFocus.current = true;
                return null;
              }
            });
          }
          break;
        case "Escape":
          setOpen(false);
          setActiveItemIndex(null);
          menuButtonNeedFocus.current = true;
          break;
        default:
          break;
      }
    }
  };

  // Focus management
  useEffect(() => {
    let animationFrameListitem: number | null = null;
    let animationFrameMenuButton: number | null = null;
    const itemList = itemListRef.current;
    const items =
      itemList &&
      Array.from(itemList.children)
        .map((li) => li.firstChild as HTMLButtonElement)
        .filter((button) => button);
    const menuButton = buttonGroupRef.current && (buttonGroupRef.current.firstChild as HTMLButtonElement);

    // Set focus on the active list item
    if (open && items && activeItemIndex !== null) {
      const activListItem = items[activeItemIndex];

      animationFrameListitem = window.requestAnimationFrame(() => {
        activListItem && activListItem.focus();
      });
    }
    // Set focus on the menu button if the menu has just been closed
    else if (menuButton && menuButtonNeedFocus.current) {
      animationFrameMenuButton = window.requestAnimationFrame(() => {
        menuButton && menuButton.focus();
      });
      menuButtonNeedFocus.current = false;
    }

    return () => {
      animationFrameListitem && window.cancelAnimationFrame(animationFrameListitem);
      animationFrameMenuButton && window.cancelAnimationFrame(animationFrameMenuButton);
    };
  }, [activeItemIndex, open]);

  const itemlistId = "dropdown-item-list-" + Math.floor(Math.random() * 10 ** 10);

  const itemListElement = (
    <ul
      role="menu"
      id={itemlistId}
      ref={itemListRef}
      className={cn("dropdown-menu__item-list", `dropdown-menu__item-list--${listPosition}`)}
    >
      {items.map((item, index) => {
        return (
          <li
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setActiveItemIndex(null);
              menuButtonNeedFocus.current = true;
            }}
            key={index}
            className={cn("dropdown-menu__item", {
              [`dropdown-menu__item--active`]: activeItemIndex === index
            })}
          >
            {item}
          </li>
        );
      })}
    </ul>
  );

  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    if (open) {
      dialogRef?.current?.showModal();
    } else {
      dialogRef?.current?.close();
    }
  }, [open]);

  return (
    <div onKeyDown={handleKeydown} className={`dropdown-menu dropdown-menu--${align}`}>
      {isMobile ? (
        <Dialog
          title=""
          ref={dialogRef}
          onClose={() => {
            setActiveItemIndex(null);
            menuButtonNeedFocus.current = true;
          }}
          className="dropdown-menu__modal"
          verticalAlignment={Alignment.Bottom}
        >
          {itemListElement}
        </Dialog>
      ) : null}
      <div ref={buttonGroupRef}>
        <IconButton
          iconName={FaIcons.EllipsisHLight}
          theme="neutral"
          variant="solid"
          onClick={handleMenuButtonClick}
          className={cn("dropdown-menu__button", {
            "dropdown-menu__button--active": open
          })}
          aria-label="Velg handling"
          aria-expanded={open}
          aria-haspopup="menu"
          ariaControls={itemlistId}
          size="medium"
        />
      </div>

      {isMobile ? null : open && itemListElement}
    </div>
  );
};

export default DropdownMenu;
